import { forwardRef, useCallback, useMemo } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { ValueInput } from 'ts-components/inputs/ValueInput';
import Cols from '__components/Layout/Cols';
import type {
  ArraySeparator,
  SourceVariableType,
  StaticVariableType,
  VariableValidation,
} from '../../types';
import { fieldValidate, FieldValidation } from '../utilties';
import { useErrors } from '../useErrors';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';
import ApplySelectButton from '__components/Inputs/Select/ApplyButton';
import { StringInput, StringInputPayload } from './StringInput';
import { useVariableContext } from '../hooks/useVariableContext';
import { OutlineInputControl } from '__components/Inputs/InputControl';
import { DateTimeTimezoneWarning } from 'ts-components/AutomationEngine/DateTimeTimezoneWarning';
import type {
  GenericOption,
  KizenFieldType,
} from 'pages/AutomationEngine/types';
import { ArrayOfStringsInput } from './ArrayOfStringsInput';
import { ARRAY_SEPARATORS } from '../../constants';

const defaultState: StaticVariableType = {
  value: '',
  valueHtml: '',
  isValid: true,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  id: Date.now().toString(),
};

type StaticOption = GenericOption<string> & {
  entityId?: string;
  fieldType?: KizenFieldType;
};

const useComponentState = (values: SourceVariableType, t: TFunction) => {
  const {
    updateVariableSource,
    variable,
    setErrors: _setErrors,
    errors: _errors,
  } = useVariableContext(values?.forInitialize!);

  const { dataType, dataSubtype, isArray } = variable ?? {};

  const { staticVariable = defaultState, id } = values;

  const { setError, errors, validate, clearError } = useErrors(
    id,
    _errors,
    _setErrors
  );
  const { value, valueHtml, isValid, arraySeparator } = staticVariable!;
  const error = errors[id];

  const updateStaticVariable = useCallback(
    (staticVariable: StaticVariableType) => {
      updateVariableSource({
        ...values,
        staticVariable,
      });
    },
    [updateVariableSource, values]
  );

  const setValue = useCallback(
    (
      value: string | string[] | StaticOption | Array<StaticOption>,
      isValid: boolean = true
    ) => {
      updateStaticVariable({ ...staticVariable, value, isValid });
      if (isValid) {
        clearError('value');
      }
    },
    [clearError, staticVariable, updateStaticVariable]
  );

  const setHtmlValue = useCallback(
    (payload: StringInputPayload, isValid: boolean = true) => {
      updateStaticVariable({
        ...staticVariable,
        value: payload.value || '',
        valueHtml: payload.valueHtml,
        isValid,
      });
      if (isValid) {
        clearError('value');
      }
    },
    [clearError, staticVariable, updateStaticVariable]
  );

  const setArraySeparator = useCallback(
    (separator: ArraySeparator, value?: string[], isValid: boolean = true) => {
      updateStaticVariable({
        ...staticVariable,
        arraySeparator: separator,
        ...(value !== undefined ? { value, isValid } : {}),
      });
      if (value !== undefined && isValid) {
        clearError('value');
      }
    },
    [clearError, staticVariable, updateStaticVariable]
  );

  const inputFormat = useMemo(() => {
    switch (dataType) {
      case AUTOMATION_VARIABLE_DATA_TYPES.date:
        return '%Y-%m-%d';

      case AUTOMATION_VARIABLE_DATA_TYPES.datetime:
        return '%Y-%m-%d %H:%M-%S-%X';

      case AUTOMATION_VARIABLE_DATA_TYPES.boolean:
        return 'true_false';

      default:
        return null;
    }
  }, [dataType]);

  return {
    value,
    valueHtml,
    setValue,
    setHtmlValue,
    arraySeparator,
    setArraySeparator,
    dataType,
    dataSubtype,
    isValid,
    error,
    setError,
    validate,
    inputFormat,
    enableAutomationVariables: !values?.forInitialize!,
    disabled: !id,
    variableIsArray: isArray,
  };
};

export const Component = forwardRef((values: SourceVariableType, ref) => {
  const { t } = useTranslation();
  const {
    value,
    setValue,
    valueHtml,
    setHtmlValue,
    arraySeparator,
    setArraySeparator,
    dataType,
    dataSubtype,
    error,
    setError,
    validate,
    inputFormat,
    enableAutomationVariables,
    disabled,
    variableIsArray,
  } = useComponentState(values, t);

  if (disabled) {
    return <OutlineInputControl label={t('Value')}>{'—'}</OutlineInputControl>;
  }

  if (
    variableIsArray &&
    (dataType === AUTOMATION_VARIABLE_DATA_TYPES.string ||
      dataType === AUTOMATION_VARIABLE_DATA_TYPES.uuid)
  ) {
    return (
      <Cols columns={1} gutter="20px">
        <ArrayOfStringsInput
          dataType={dataType}
          value={value as string[]}
          onChange={setValue}
          error={error?.value}
          validate={validate}
          setError={setError}
          disabled={false}
          arraySeparator={arraySeparator ?? ARRAY_SEPARATORS.comma}
          setArraySeparator={setArraySeparator}
        />
      </Cols>
    );
  }

  return dataType !== AUTOMATION_VARIABLE_DATA_TYPES.string ? (
    <Cols columns={variableIsArray ? 1 : 3} gutter="20px">
      <div>
        <ValueInput
          error={error?.value}
          validate={validate}
          inputFormat={inputFormat}
          setError={setError}
          allowEmpty
          menuInline
          data-qa="static-value"
          menuLeftButton={<ClearSelectButton onClick={() => setValue('')} />}
          menuRightButton={<ApplySelectButton />}
          onChange={setValue}
          {...(dataType === AUTOMATION_VARIABLE_DATA_TYPES.entity
            ? {
                dataType,
                customObjectId: dataSubtype?.value!,
                value: variableIsArray
                  ? (value! as GenericOption[])
                  : (value! as GenericOption),
              }
            : dataType === AUTOMATION_VARIABLE_DATA_TYPES.employee
              ? {
                  dataType,
                  value: value! as GenericOption,
                }
              : {
                  dataType,
                  value: value! as string,
                })}
          isArray={variableIsArray}
        />
        {dataType === AUTOMATION_VARIABLE_DATA_TYPES.datetime && value ? (
          <DateTimeTimezoneWarning dateValue={value as string} />
        ) : null}
      </div>
    </Cols>
  ) : (
    <Cols columns={1} gutter="20px">
      <StringInput
        value={value as string}
        title={t('Value')}
        valueHtml={valueHtml}
        onChange={setHtmlValue}
        error={error?.value}
        validate={validate}
        setError={setError}
        dataQa="static-value"
        disabled={false}
        enableAutomationVariables={enableAutomationVariables}
      />
    </Cols>
  );
});

const getFieldsToValidate = (t: TFunction): FieldValidation => ({
  value: [
    {
      type: 'deleted',
      fieldType: 'entity',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Record'), fallback: t('Team Member') },
    },
    {
      type: 'deleted',
      fieldType: 'employee',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Record'), fallback: t('Team Member') },
    },
  ],
});

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  const { staticVariable, sourceSubtype } = values;

  return fieldValidate(
    staticVariable,
    getFieldsToValidate(t),
    sourceSubtype,
    checkRequired,
    t
  );
};
